import React, { useState, useEffect } from "react";
import {
    Truck, Plus, FileText, Eye, Trash2, X, Upload,
    PackageCheck, Loader2, AlertCircle, ShoppingBag, Search,
    Tag, CheckSquare, Edit3, Download, Calendar, User
} from "lucide-react";
import api from "../api/axiosInstance";

const ChallanManagement = () => {
    // Data States
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [allReceipts, setAllReceipts] = useState([]);

    // UI Selection States
    const [selectedPO, setSelectedPO] = useState(null);
    const [poItems, setPoItems] = useState([]);
    const [challans, setChallans] = useState([]);

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingChallan, setViewingChallan] = useState(null);
    const [viewingItems, setViewingItems] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [isDirect, setIsDirect] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const initialForm = {
        id: "",
        challan_no: "",
        received_date: new Date().toISOString().split("T")[0],
        received_by: "",
        notes: "",
        document: null,
        vendor_id: "",
        project_id: "",
    };
    const [formData, setFormData] = useState(initialForm);
    const [directItems, setDirectItems] = useState([{ id: Date.now(), item_name: "", qty: "", unit: "pcs" }]);

    useEffect(() => {
        loadInitialData();
        loadReceipts();
    }, []);

    const loadInitialData = async () => {
        try {
            const [poRes, vRes, pRes] = await Promise.all([
                api.get("get_purchase_orders.php"),
                api.get("get_directory.php?type=vendor"),
                api.get("get_projects.php"),
            ]);
            setPurchaseOrders(poRes.data.data || []);
            setVendors(vRes.data.data || []);
            setProjects(pRes.data.projects || []);
        } catch (err) { console.error("Data Load Error", err); }
    };

    const loadReceipts = async () => {
        try {
            const res = await api.get("get_all_site_receipts.php");
            setAllReceipts(res.data.data || []);
        } catch (err) { console.error("Receipts Load Error", err); }
    };

    const loadFulfillmentData = async (poId) => {
        setLoading(true);
        try {
            const res = await api.get(`get_po_fulfillment.php?po_id=${poId}`);
            // We set current_receipt to empty so user can fill fresh numbers
            const items = (res.data.items || []).map(item => ({ ...item, current_receipt: "" }));
            setPoItems(items);
            setChallans(res.data.challans || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };
    // 1. Add this function inside your component
    const fetchNextChallanNo = async () => {
        try {
            const res = await api.get("get_next_challan_no.php");
            if (res.data.status === "success") {
                setFormData(prev => ({ ...prev, challan_no: res.data.next_no }));
            }
        } catch (err) {
            console.error("Auto-gen failed", err);
        }
    };
    const handlePOSelection = (id) => {
        if (!id) { setSelectedPO(null); return; }
        const po = purchaseOrders.find(p => p.id == id);
        setSelectedPO(po);
        setIsDirect(false);
        loadFulfillmentData(id);
    };

    const openViewModal = async (receipt) => {
        setViewingChallan(receipt);
        try {
            const res = await api.get(`get_challan_details.php?id=${receipt.id}`);
            setViewingItems(res.data.items || []);
            setIsViewModalOpen(true);
        } catch (err) { alert("Error loading details"); }
    };

    const handleEdit = async (receipt) => {
        setIsEdit(true);
        setIsDirect(!receipt.po_id);
        setFormData({
            id: receipt.id,
            challan_no: receipt.challan_no,
            received_date: receipt.received_date,
            received_by: receipt.received_by,
            notes: receipt.notes || "",
            vendor_id: receipt.vendor_id || "",
            project_id: receipt.project_id || "",
            document: null
        });

        try {
            const res = await api.get(`get_challan_details.php?id=${receipt.id}`);
            if (receipt.po_id) {
                const po = purchaseOrders.find(p => p.id == receipt.po_id);
                setSelectedPO(po);
                // For editing, we map the quantity currently in this challan to the input
                setPoItems(res.data.items.map(item => ({ ...item, current_receipt: item.received_qty, item_name: item.item_name })));
            } else {
                setDirectItems(res.data.items.map(item => ({ ...item, item_name: item.item_name, qty: item.received_qty })));
            }
            setIsModalOpen(true);
        } catch (err) { alert("Error loading edit data"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Move this challan to trash? Site inventory will be adjusted.")) return;
        try {
            await api.post("manage_delivery_challan.php", { id, action: 'delete' });
            loadReceipts();
            if (selectedPO) loadFulfillmentData(selectedPO.id);
        } catch (err) { alert("Delete failed"); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append("action", isEdit ? "update" : "create");
        if (isEdit) data.append("id", formData.id);

        if (!isDirect) {
            if (!selectedPO) return alert("Select a PO");
            data.append("po_id", selectedPO.id);
            const itemsToSave = poItems.filter(i => i.current_receipt > 0).map(i => ({
                po_item_id: i.po_item_id || i.id,
                received_qty: i.current_receipt,
                item_name: i.item_name
            }));
            data.append("items", JSON.stringify(itemsToSave));
        } else {
            data.append("vendor_id", formData.vendor_id);
            data.append("project_id", formData.project_id);
            data.append("direct_items", JSON.stringify(directItems));
        }

        data.append("is_direct", isDirect ? 1 : 0);
        data.append("challan_no", formData.challan_no);
        data.append("received_date", formData.received_date);
        data.append("received_by", formData.received_by);
        data.append("notes", formData.notes);
        if (formData.document) data.append("document", formData.document);

        try {
            const abc = await api.post("manage_delivery_challan.php", data, { headers: { "Content-Type": "multipart/form-data" } });
            console.log(abc);
            setIsModalOpen(false);
            loadReceipts();
            if (selectedPO) loadFulfillmentData(selectedPO.id);
            alert("Record Saved!");
        } catch (err) {
            console.log(err);
            alert("Save Error");
        }
    };

    const filteredReceipts = allReceipts.filter(r =>
        r.challan_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 p-4 bg-slate-50/50 min-h-screen">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 italic uppercase tracking-tighter">Site Material Receipts</h1>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Inward Logistics & PO Fulfillment</p>
                </div>
                <div className="flex gap-3">
                    <select
                        className="p-4 bg-white border-2 border-slate-900 rounded-2xl font-black text-[10px] uppercase outline-none shadow-sm"
                        onChange={(e) => handlePOSelection(e.target.value)}
                        value={selectedPO?.id || ""}
                    >
                        <option value="">Link to Purchase Order...</option>
                        {purchaseOrders.map((p) => <option key={p.id} value={p.id}>{p.po_no} - {p.vendor_name}</option>)}
                    </select>
                    <button
                        onClick={() => {
                            fetchNextChallanNo(); // <--- Call here for Direct Entry
                            setIsEdit(false); setIsDirect(true); setFormData(initialForm); setDirectItems([{ id: Date.now(), item_name: "", qty: "", unit: "pcs" }]); setIsModalOpen(true);
                        }}
                        className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2"
                    >
                        <ShoppingBag size={18} /> Direct Receipt
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Left Side: Recent Activity & Search */}
                <div className="xl:col-span-2 space-y-6">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search by challan, vendor, or site name..."
                            className="w-full p-5 pl-12 bg-white border border-slate-100 rounded-[30px] font-bold text-sm outline-none shadow-sm focus:ring-2 ring-indigo-500/20 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b">
                                <tr>
                                    <th className="p-6">Origin</th>
                                    <th className="p-6">Challan / Date</th>
                                    <th className="p-6">Project Site</th>
                                    <th className="p-6 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filteredReceipts.map((receipt) => (
                                    <tr key={receipt.id} className="group hover:bg-slate-50/50 transition-colors">
                                        <td className="p-6">
                                            <span className={`px-3 py-1 rounded-full font-black text-[8px] uppercase ${receipt.po_id ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                                {receipt.po_id ? 'PO Linked' : 'Direct Call'}
                                            </span>
                                        </td>
                                        <td className="p-6">
                                            <p className="font-black text-slate-900 italic uppercase">#{receipt.challan_no}</p>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{receipt.received_date}</p>
                                        </td>
                                        <td className="p-6">
                                            <div className="flex items-center gap-2">
                                                <Tag size={12} className="text-indigo-400" />
                                                <span className="font-black text-slate-900 text-[11px] uppercase truncate max-w-[150px]">{receipt.project_name}</span>
                                            </div>
                                        </td>
                                        <td className="p-6 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => openViewModal(receipt)} className="p-3 bg-slate-50 text-slate-400 hover:text-indigo-600 rounded-xl transition-all"><Eye size={16} /></button>
                                                <button onClick={() => handleEdit(receipt)} className="p-3 bg-slate-50 text-slate-400 hover:text-amber-500 rounded-xl transition-all"><Edit3 size={16} /></button>
                                                <button onClick={() => handleDelete(receipt.id)} className="p-3 bg-slate-50 text-slate-400 hover:text-rose-500 rounded-xl transition-all"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right Side: Fulfillment Status */}
                <div className="space-y-6">
                    {selectedPO ? (
                        <div className="bg-slate-900 rounded-[40px] p-8 text-white shadow-2xl sticky top-4">
                            <div className="flex justify-between items-start mb-8">
                                <div>
                                    <h3 className="text-2xl font-black italic tracking-tighter leading-none">Fulfillment</h3>
                                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-2">Tracking PO: {selectedPO.po_no}</p>
                                </div>
                                <button onClick={() => {
                                    fetchNextChallanNo(); // <--- Call here for Direct Entry
                                    setIsEdit(false); setIsDirect(false); setIsModalOpen(true);
                                }} className="bg-indigo-600 p-3 rounded-2xl hover:scale-105 transition-transform">
                                    <Plus size={20} />
                                </button>
                            </div>

                            <div className="space-y-5">
                                {poItems.map(item => {
                                    const pending = parseFloat(item.qty) - parseFloat(item.already_received);
                                    const progress = (parseFloat(item.already_received) / parseFloat(item.qty)) * 100;
                                    return (
                                        <div key={item.id} className="bg-white/5 p-5 rounded-3xl border border-white/10">
                                            <div className="flex justify-between items-center mb-3">
                                                <p className="text-[11px] font-black uppercase italic text-slate-200">{item.item_name}</p>
                                                <p className="text-[10px] font-black text-indigo-300">{item.already_received} / {item.qty} {item.unit}</p>
                                            </div>
                                            <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden mb-2">
                                                <div className={`h-full transition-all duration-700 ${pending <= 0 ? 'bg-emerald-400' : 'bg-indigo-500'}`} style={{ width: `${Math.min(progress, 100)}%` }} />
                                            </div>
                                            <p className={`text-[8px] font-black uppercase tracking-widest ${pending <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                {pending <= 0 ? 'Fully Fulfilled' : `${pending} ${item.unit} Pending`}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-[40px] p-10 border-4 border-dashed border-slate-100 flex flex-col items-center justify-center text-center">
                            <Truck size={60} className="text-slate-200 mb-4" />
                            <p className="font-black text-xs uppercase tracking-widest text-slate-300 leading-relaxed">Select a Purchase Order to view live site fulfillment progress</p>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL: CREATE / EDIT */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[500] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    <form onSubmit={handleSubmit} className="bg-white w-full max-w-4xl rounded-[48px] p-10 space-y-8 overflow-y-auto max-h-[95vh] shadow-2xl">
                        <div className="flex justify-between items-center border-b pb-6">
                            <div>
                                <h2 className="text-3xl font-black italic uppercase tracking-tighter">{isEdit ? "Update" : "New"} Site Receipt</h2>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isDirect ? "Direct Delivery Log" : `Fulfilling PO: ${selectedPO?.po_no}`}</p>
                            </div>
                            <button type="button" onClick={() => setIsModalOpen(false)} className="p-4 bg-slate-50 rounded-full hover:bg-rose-50 text-rose-500 transition-colors"><X /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {isDirect && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase text-indigo-500 ml-4">Select Vendor</label>
                                        <select required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={formData.vendor_id} onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}>
                                            <option value="">Choose Supplier...</option>
                                            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase text-indigo-500 ml-4">Select Project Site</label>
                                        <select required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}>
                                            <option value="">Choose Site...</option>
                                            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                        </select>
                                    </div>
                                </>
                            )}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 ml-4">Challan / Invoice No</label>
                                <input required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-indigo-100 transition-all" value={formData.challan_no} onChange={(e) => setFormData({ ...formData, challan_no: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 ml-4">Date Received</label>
                                <input type="date" required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={formData.received_date} onChange={(e) => setFormData({ ...formData, received_date: e.target.value })} />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-[11px] font-black uppercase text-indigo-600 tracking-wider ml-2">Material Quantity Receipt</h3>
                            {!isDirect ? (
                                <div className="grid grid-cols-1 gap-3">
                                    {poItems.map((item, idx) => (
                                        <div key={item.id} className="flex items-center gap-4 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                            <div className="flex-1">
                                                <p className="font-black text-slate-900 text-sm italic uppercase tracking-tighter">{item.item_name}</p>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase">Total Ordered: {item.qty} {item.unit}</p>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    className="w-24 p-4 bg-white rounded-2xl font-black text-center text-indigo-600 border-2 border-transparent focus:border-indigo-500 outline-none"
                                                    value={item.current_receipt}
                                                    onChange={(e) => {
                                                        const n = [...poItems]; n[idx].current_receipt = e.target.value; setPoItems(n);
                                                    }}
                                                />
                                                <span className="text-[10px] font-black uppercase text-slate-400">{item.unit}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {directItems.map((item, idx) => (
                                        <div key={item.id} className="grid grid-cols-12 gap-3 items-center bg-slate-50 p-4 rounded-3xl">
                                            <input className="col-span-8 p-4 bg-white rounded-2xl font-bold outline-none" placeholder="Description of goods..." value={item.item_name} onChange={(e) => {
                                                const n = [...directItems]; n[idx].item_name = e.target.value; setDirectItems(n);
                                            }} />
                                            <input type="number" className="col-span-3 p-4 bg-white rounded-2xl font-bold text-center outline-none" placeholder="Qty" value={item.qty} onChange={(e) => {
                                                const n = [...directItems]; n[idx].qty = e.target.value; setDirectItems(n);
                                            }} />
                                            <button type="button" onClick={() => setDirectItems(directItems.filter(i => i.id !== item.id))} className="col-span-1 text-rose-400 hover:scale-110 transition-transform"><Trash2 size={20} /></button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => setDirectItems([...directItems, { id: Date.now(), item_name: "", qty: "", unit: "pcs" }])} className="text-[10px] font-black text-indigo-600 uppercase mt-2 ml-4 hover:underline">+ Add Another Item</button>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 ml-4">Notes / Remarks</label>
                                <textarea className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none resize-none" rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                            </div>
                            <div className="relative border-2 border-dashed border-slate-200 rounded-3xl p-6 text-center hover:border-indigo-400 group transition-all">
                                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => setFormData({ ...formData, document: e.target.files[0] })} />
                                <Upload className="mx-auto text-slate-300 mb-2 group-hover:text-indigo-500 transition-colors" size={24} />
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{formData.document ? formData.document.name : "Upload Physical Challan Copy"}</p>
                            </div>
                        </div>

                        <button className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl uppercase italic shadow-2xl hover:bg-indigo-600 transition-all transform hover:-translate-y-1 active:scale-95">
                            {isEdit ? "Update Transaction" : "Confirm Site Inward"}
                        </button>
                    </form>
                </div>
            )}

            {/* MODAL: VIEW DETAILS */}
            {isViewModalOpen && viewingChallan && (
                <div className="fixed inset-0 z-[600] bg-slate-900/95 backdrop-blur-xl flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl rounded-[50px] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-10 border-b flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="text-3xl font-black italic uppercase leading-none">Receipt Summary</h2>
                                <p className="text-[11px] font-bold text-indigo-500 mt-2 uppercase tracking-widest">Challan #{viewingChallan.challan_no}</p>
                            </div>
                            <button onClick={() => setIsViewModalOpen(false)} className="p-4 bg-white rounded-2xl shadow-sm text-slate-400 hover:text-rose-500 transition-colors"><X size={24} /></button>
                        </div>
                        <div className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-10">
                                <div>
                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Received Date</p>
                                    <p className="font-bold text-slate-900 italic text-lg">{viewingChallan.received_date}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Project Site</p>
                                    <p className="font-bold text-slate-900 italic text-lg">{viewingChallan.project_name}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 mb-4">Material Breakdown</p>
                                <div className="bg-slate-50 rounded-[30px] p-8 space-y-4">
                                    {viewingItems.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center border-b border-slate-200/50 pb-3 last:border-0 last:pb-0">
                                            <span className="font-black text-sm uppercase italic text-slate-700">{item.item_name}</span>
                                            <span className="font-black text-indigo-600">{item.received_qty} {item.unit || 'pcs'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {viewingChallan.document_path && (
                                <a
                                    href={`${api.defaults.baseURL}${viewingChallan.document_path}`}
                                    target="_blank"
                                    className="flex items-center justify-center gap-3 p-6 bg-slate-900 text-white rounded-[30px] font-black uppercase text-xs italic tracking-widest hover:bg-indigo-600 transition-all shadow-xl"
                                >
                                    <Download size={18} /> View Signed Copy
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChallanManagement;